import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define types
export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  companyId?: string;
  companyName?: string;
}

// Import Permission interface
import { Permission } from '../../utils/getUsersRoles';

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  companyId: string | null;
  permissions: Permission[] | null;
  companyContext: {
    currentCompanyId: string | null;
    availableCompanies: Array<{ id: string, name: string }> | null;
  };
}

// Define initial state
const initialState: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  companyId: null,
  permissions: null,
  companyContext: {
    currentCompanyId: null,
    availableCompanies: null,
  },
};

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; accessToken: string; companyId?: string }>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      
      // Set company context if available
      if (action.payload.companyId) {
        state.companyId = action.payload.companyId;
        state.companyContext.currentCompanyId = action.payload.companyId;
        
        // If user has a company name, add it to available companies
        if (action.payload.user.companyId && action.payload.user.companyName) {
          state.companyContext.availableCompanies = [
            { id: action.payload.user.companyId, name: action.payload.user.companyName }
          ];
        }
      }
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.companyId = null;
      state.companyContext = {
        currentCompanyId: null,
        availableCompanies: null,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    // Set company context
    setCompanyContext: (state, action: PayloadAction<{
      currentCompanyId: string;
      availableCompanies?: Array<{ id: string, name: string }>
    }>) => {
      state.companyId = action.payload.currentCompanyId;
      state.companyContext.currentCompanyId = action.payload.currentCompanyId;
      
      if (action.payload.availableCompanies) {
        state.companyContext.availableCompanies = action.payload.availableCompanies;
      }
    },
    
    // Switch company (for users with access to multiple companies)
    switchCompany: (state, action: PayloadAction<string>) => {
      state.companyId = action.payload;
      state.companyContext.currentCompanyId = action.payload;
    },
    
    // Set user permissions
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
    },
  },
});

// Export actions and reducer
export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  setCompanyContext,
  switchCompany,
  setPermissions
} = authSlice.actions;
export default authSlice.reducer;